import { useState, useEffect } from "react";
import {
  Sidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarFooter,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Star, ChevronDown, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";

import { useFavorites } from "./hooks/useFavorites";
import { CompanySwitcher } from "./components/CompanySwitcher";
import { FavoritesSection } from "./components/FavoritesSection";
import { MenuItemWithContext } from "./components/MenuItemWithContext";
import { LeftMenuProps, MenuItem } from "./types";
import { getMenuItems, companies, getMenuConfig } from "./config/menuItems";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { useMode } from "@/providers/ModeProvider";

/**
 * Helper function to render Phase 1 badge for menu items
 */
const renderPhaseBadge = (item: MenuItem) => {
  if (!item.phase || item.phase !== "phase1") return null;
  
  return (
    <Badge 
      variant="default" 
      className="ml-auto text-[9px] px-1.5 py-0.5 h-[18px] bg-green-600 hover:bg-green-600 text-white font-semibold shrink-0 whitespace-nowrap"
    >
      Phase 1
    </Badge>
  );
};

/**
 * Left Menu Component (Modular Version)
 * 
 * Features:
 * - Collapsible sidebar (icon-only mode)
 * - Favorites system (enabled for 50+ pages)
 * - Company selector in footer
 * - Context menu for favoriting items
 * - Nested menu items support
 * - Mobile responsive
 * - Keyboard shortcuts (Cmd/Ctrl + B)
 */
export function LeftMenuModular({ activePage, onPageChange }: LeftMenuProps) {
  const [selectedCompany, setSelectedCompany] = useState("production");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Get the mode for warehouse-location-setup screen to determine if we should fetch from API
  const { isScreenLive } = useMode();
  const isWarehouseSetupLive = isScreenLive("warehouse-location-setup");
  
  // Get level 1 label from warehouse hierarchy config
  const { getLevelDisplayName } = useWarehouseHierarchyConfig({ isLive: isWarehouseSetupLive });
  const level1Label = getLevelDisplayName(1);
  
  // Get dynamic menu items based on feature flags and custom labels
  const menuItems = getMenuItems({ level1Label });
  const menuConfig = getMenuConfig();
  
  // Track which parent sections are open
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    inventory: false,
    purchasing: false,
    salesFulfillment: false,
    masterData: false,
    manufacturing: false,
    aiForecastingAnalytics: false,
    pricing: false,
    settings: false,
  });

  const { state, open, setOpen, isMobile, openMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";

  const favoritesEnabled = menuConfig.totalPages >= menuConfig.favoritesThreshold;
  const { favorites, toggleFavorite, isFavorite } = useFavorites(favoritesEnabled);

  // Get all items including children for favorites
  const allItems = menuItems.flatMap((item) => [item, ...(item.children || [])]);
  const favoriteItems = allItems.filter((item) => isFavorite(item.id));

  // Filter menu items based on search query
  const filterMenuItems = (items: typeof menuItems, query: string) => {
    if (!query.trim()) return items;
    
    const lowerQuery = query.toLowerCase();
    
    return items.map((item) => {
      // Check if parent label matches
      const parentMatches = item.label.toLowerCase().includes(lowerQuery);
      
      // Filter children that match
      const matchingChildren = item.children?.filter((child) =>
        child.label.toLowerCase().includes(lowerQuery)
      );
      
      // Include item if parent matches or has matching children
      if (parentMatches || (matchingChildren && matchingChildren.length > 0)) {
        return {
          ...item,
          children: parentMatches ? item.children : matchingChildren,
        };
      }
      
      return null;
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  };

  const filteredMenuItems = filterMenuItems(menuItems, searchQuery);

  // Auto-expand sections when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = filterMenuItems(menuItems, searchQuery);
      const newOpenSections: Record<string, boolean> = {};
      filtered.forEach((item) => {
        if (item.children && item.children.length > 0) {
          const sectionKey = item.id === "sales" ? "salesFulfillment" : 
                           item.id === "master-data" ? "masterData" :
                           item.id === "ai" ? "aiForecastingAnalytics" :
                           item.id;
          newOpenSections[sectionKey] = true;
        }
      });
      setOpenSections(newOpenSections);
    }
  }, [searchQuery]);

  // Sync activeItemId when activePage changes (e.g., from breadcrumb navigation)
  useEffect(() => {
    // Find the menu item that matches the activePage
    const findItemId = (page: string): string | null => {
      // Check top-level items (exact match)
      const topLevelItem = menuItems.find((item) => item.page === page);
      if (topLevelItem) {
        return `main-${topLevelItem.id}`;
      }
      
      // Check children (exact match)
      for (const parent of menuItems) {
        if (parent.children) {
          const childItem = parent.children.find((child) => child.page === page);
          if (childItem) {
            return `main-${childItem.id}`;
          }
        }
      }
      
      // Check for sub-pages (e.g., "stock-takes/targeted/setup" should match "stock-takes")
      // This handles navigation to sub-pages that should highlight their parent menu item
      for (const parent of menuItems) {
        if (parent.children) {
          const childItem = parent.children.find((child) => page.startsWith(child.page + "/"));
          if (childItem) {
            return `main-${childItem.id}`;
          }
        }
      }
      
      return null;
    };
    
    const itemId = findItemId(activePage);
    if (itemId) {
      setActiveItemId(itemId);
    }
  }, [activePage]);

  const handleItemClick = (page: string, itemId: string, parentSection?: string) => {
    // Store the current collapsed state before any changes
    const wasCollapsed = isCollapsed;
    
    // If sidebar is collapsed, expand it
    if (wasCollapsed) {
      if (isMobile) {
        setOpenMobile(true);
      } else {
        setOpen(true);
      }
    }
    
    // If this is a nested item and sidebar was collapsed, open its parent section
    if (wasCollapsed && parentSection) {
      // Use setTimeout to ensure the sidebar expansion happens first
      setTimeout(() => {
        setOpenSections((prev) => ({ ...prev, [parentSection]: true }));
      }, 50);
    }
    
    setActiveItemId(itemId);
    onPageChange(page);
  };

  const renderMenuItem = (item: any, uniqueId: string, isFavoriteItem: boolean = false) => {
    const isActive = activeItemId === uniqueId;
    return (
      <MenuItemWithContext
        item={item}
        uniqueId={uniqueId}
        isActive={isActive}
        isFavoriteItem={isFavoriteItem}
        isFavorite={isFavorite(item.id)}
        onItemClick={handleItemClick}
        onToggleFavorite={toggleFavorite}
        favoritesEnabled={favoritesEnabled}
      />
    );
  };

  return (
    <Sidebar variant="floating" collapsible="icon" className="sticky top-0 h-screen">
      {/* Header */}
      <SidebarHeader className="pt-2 flex-shrink-0">
        <div className="flex items-center gap-2 pl-2">
          <SidebarTrigger />
          <h3 className="font-semibold truncate whitespace-nowrap max-w-[140px] group-data-[state=collapsed]:hidden text-neutral-700">
            Stock Evo
          </h3>
          <div className="ml-auto mr-2 group-data-[state=collapsed]:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
            >
              {isSearchOpen ? <X className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </SidebarHeader>
      <div className="border-t border-neutral-200 mx-2 group-data-[state=collapsed]:show flex-shrink-0"></div>
      
      {/* Search Input */}
      {isSearchOpen && (
        <div className="px-3 py-2 group-data-[state=collapsed]:hidden">
          <Input
            type="text"
            placeholder="Search pages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8"
            autoFocus
          />
        </div>
      )}

      {/* Content */}
      <SidebarContent className="group-data-[state=collapsed]:items-center flex-1 overflow-y-auto">
        {/* Favorites Section */}
        {favoritesEnabled && (
          <FavoritesSection favoriteItems={favoriteItems} renderMenuItem={renderMenuItem} />
        )}

        {/* Main Navigation Section */}
        <SidebarGroup className="group-data-[state=collapsed]:items-center">
          <SidebarGroupContent className="group-data-[state=collapsed]:w-8">
            <SidebarMenu className="gap-0.5 group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
              
              {/* Dashboard - Top Level */}
              {filteredMenuItems.find((item) => item.id === "dashboard") && (
                <SidebarMenuItem>
                  {renderMenuItem(filteredMenuItems.find((item) => item.id === "dashboard")!, "main-dashboard")}
                </SidebarMenuItem>
              )}

              {/* Inventory - Nested */}
              {filteredMenuItems.find((item) => item.id === "inventory") && (
                <Collapsible 
                  asChild 
                  open={openSections.inventory}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, inventory: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Inventory"
                        onClick={() => {
                          // If collapsed, expand sidebar and open this section
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, inventory: true }));
                            }, 50);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "inventory")!.icon;
                          return <Icon />;
                        })()}
                        <span className="flex-1">Inventory</span>
                        {renderPhaseBadge(filteredMenuItems.find((item) => item.id === "inventory")!)}
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "inventory")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "inventory")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "inventory")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}

              {/* Purchasing - Nested */}
              {filteredMenuItems.find((item) => item.id === "purchasing") && (
                <Collapsible 
                  asChild 
                  open={openSections.purchasing}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, purchasing: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Purchasing"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, purchasing: true }));
                            }, 50);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "purchasing")!.icon;
                          return <Icon />;
                        })()}
                        <span className="flex-1">Purchasing</span>
                        {renderPhaseBadge(filteredMenuItems.find((item) => item.id === "purchasing")!)}
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "purchasing")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "purchasing")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "purchasing")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}

              {/* Sales & Fulfillment - Nested */}
              {filteredMenuItems.find((item) => item.id === "sales") && (
                <Collapsible 
                  asChild 
                  open={openSections.salesFulfillment}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, salesFulfillment: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Sales & Fulfillment"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, salesFulfillment: true }));
                            }, 50);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "sales")!.icon;
                          return <Icon />;
                        })()}
                        <span className="flex-1">Sales & Fulfillment</span>
                        {renderPhaseBadge(filteredMenuItems.find((item) => item.id === "sales")!)}
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "sales")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "salesFulfillment")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "salesFulfillment")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}

              {/* Master Data - Nested */}
              {filteredMenuItems.find((item) => item.id === "master-data") && (
                <Collapsible 
                  asChild 
                  open={openSections.masterData}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, masterData: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Master Data"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, masterData: true }));
                            }, 50);
                          }
                        }}
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "master-data")!.icon;
                          return <Icon />;
                        })()}
                        <span>Master Data</span>
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "master-data")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "masterData")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "masterData")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              )}

              {/* Manufacturing - Nested */}
              {filteredMenuItems.find((item) => item.id === "manufacturing") && (
                <Collapsible 
                  asChild 
                  open={openSections.manufacturing}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, manufacturing: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Manufacturing"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, manufacturing: true }));
                            }, 50);
                          }
                        }}
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "manufacturing")!.icon;
                          return <Icon />;
                        })()}
                        <span>Manufacturing</span>
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "manufacturing")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "manufacturing")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "manufacturing")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              )}

              {/* AI Forecasting & Analytics - Nested */}
              {filteredMenuItems.find((item) => item.id === "ai") && (
                <Collapsible 
                  asChild 
                  open={openSections.aiForecastingAnalytics}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, aiForecastingAnalytics: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="AI Forecasting & Analytics"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, aiForecastingAnalytics: true }));
                            }, 50);
                          }
                        }}
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "ai")!.icon;
                          return <Icon />;
                        })()}
                        <span>AI Forecasting & Analytics</span>
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "ai")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "aiForecastingAnalytics")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "aiForecastingAnalytics")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              )}

              {/* Pricing - Nested */}
              {filteredMenuItems.find((item) => item.id === "pricing") && (
                <Collapsible 
                  asChild 
                  open={openSections.pricing}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, pricing: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Pricing"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, pricing: true }));
                            }, 50);
                          }
                        }}
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "pricing")!.icon;
                          return <Icon />;
                        })()}
                        <span>Pricing</span>
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "pricing")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "pricing")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "pricing")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              )}

              {/* Settings - Nested */}
              {filteredMenuItems.find((item) => item.id === "settings") && (
                <Collapsible 
                  asChild 
                  open={openSections.settings}
                  onOpenChange={(open) => setOpenSections((prev) => ({ ...prev, settings: open }))}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip="Settings"
                        onClick={() => {
                          if (isCollapsed) {
                            if (isMobile) {
                              setOpenMobile(true);
                            } else {
                              setOpen(true);
                            }
                            setTimeout(() => {
                              setOpenSections((prev) => ({ ...prev, settings: true }));
                            }, 50);
                          }
                        }}
                        className="flex items-center gap-2"
                      >
                        {(() => {
                          const Icon = filteredMenuItems.find((item) => item.id === "settings")!.icon;
                          return <Icon />;
                        })()}
                        <span className="flex-1">Settings</span>
                        {renderPhaseBadge(filteredMenuItems.find((item) => item.id === "settings")!)}
                        <ChevronDown className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filteredMenuItems.find((item) => item.id === "settings")!.children!.map((child) => (
                        <SidebarMenuSubItem key={child.id}>
                          {favoritesEnabled ? (
                            <ContextMenu>
                              <ContextMenuTrigger asChild>
                                <SidebarMenuSubButton
                                  onClick={() => handleItemClick(child.page, `main-${child.id}`, "settings")}
                                  isActive={activeItemId === `main-${child.id}`}
                                  className="flex items-center gap-2"
                                >
                                  <span className="flex-1">{child.label}</span>
                                  {renderPhaseBadge(child)}
                                </SidebarMenuSubButton>
                              </ContextMenuTrigger>
                              <ContextMenuContent>
                                <ContextMenuItem onClick={() => toggleFavorite(child.id)}>
                                  <Star className={isFavorite(child.id) ? "fill-current" : ""} />
                                  {isFavorite(child.id) ? "Remove from favorites" : "Set as favorite"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          ) : (
                            <SidebarMenuSubButton
                              onClick={() => handleItemClick(child.page, `main-${child.id}`, "settings")}
                              isActive={activeItemId === `main-${child.id}`}
                              className="flex items-center gap-2"
                            >
                              <span className="flex-1">{child.label}</span>
                              {renderPhaseBadge(child)}
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              )}

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="flex-shrink-0">
        {/* Company Switcher */}
        {menuConfig.showDatabaseSwitcher && (
          <>
            <div className="border-t border-neutral-200 mb-2 mt-2 group-data-[state=collapsed]:hidden"></div>
            <CompanySwitcher
              companies={companies}
              selectedCompany={selectedCompany}
              onCompanyChange={setSelectedCompany}
              label="Company"
            />
          </>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
